/**
 * MktgSection — reusable section wrapper for marketing pages.
 * Renders a <section> element with the appropriate background class
 * (white/neutral/dark) and standard vertical padding (--mktg-section-py).
 * Includes an inner container div limited to --mktg-container-max width
 * with --mktg-container-px horizontal padding.
 *
 * For sections that need non-standard horizontal padding or full-bleed
 * inner layouts, pass containerClassName/containerStyle to override defaults.
 */

import React from 'react';
import { cn } from '@/lib/utils/utils';

/** Background maps to .mktg-section-{background} CSS class */
type SectionBackground = 'white' | 'neutral' | 'dark';

interface MktgSectionProps {
  background?: SectionBackground;
  /** Custom section-level classes (e.g. extra margin, overflow-hidden) */
  className?: string;
  /** Custom inner container classes */
  containerClassName?: string;
  /** Override inner container inline styles */
  containerStyle?: React.CSSProperties;
  /** Accessible label for the section element */
  'aria-label'?: string;
  /** HTML id attribute for anchor linking */
  id?: string;
  children: React.ReactNode;
}

const backgroundClass: Record<SectionBackground, string> = {
  white: 'mktg-section-white',
  neutral: 'mktg-section-neutral',
  dark: 'mktg-section-dark'
};

export function MktgSection({
  background = 'white',
  className,
  containerClassName,
  containerStyle,
  'aria-label': ariaLabel,
  id,
  children
}: MktgSectionProps) {
  return (
    <section
      id={id}
      aria-label={ariaLabel}
      className={cn('py-12 md:py-20', backgroundClass[background], className)}
    >
      <div
        className={cn('mx-auto px-6 md:px-[72px]', containerClassName)}
        style={{
          maxWidth: 'var(--mktg-container-max, 1440px)',
          ...containerStyle
        }}
      >
        {children}
      </div>
    </section>
  );
}
